import {
  User,
  Player,
  Match,
  MatchListItem,
  GeneratedTeams,
  LoginRequest,
  RegisterRequest,
  TeamSize,
  ApiError,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

console.log('[ApiClient] VITE_API_URL:', import.meta.env.VITE_API_URL);
console.log('[ApiClient] API_BASE_URL:', API_BASE_URL);

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log('[ApiClient] Request URL:', url);

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include cookies
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData: ApiError = await response.json();
        console.error('[ApiClient] Error response:', errorData);
        throw new Error(errorData.error.message || 'Request failed');
      }

      return await response.json();
    } catch (error) {
      console.error('[ApiClient] Fetch error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  // Auth endpoints
  async register(data: RegisterRequest): Promise<{ success: boolean; user: User }> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(data: LoginRequest): Promise<{ success: boolean; user: User; token: string }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout(): Promise<{ success: boolean }> {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser(): Promise<{ success: boolean; user: User }> {
    return this.request('/user/me');
  }

  async verifyEmail(token: string): Promise<{ success: boolean }> {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async forgotPassword(email: string): Promise<{ success: boolean; message: string; reset_token?: string }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(email: string, token: string, new_password: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, token, new_password }),
    });
  }

  async changePassword(current_password: string, new_password: string): Promise<{ success: boolean; message: string }> {
    return this.request('/user/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password, new_password }),
    });
  }

  async changeEmail(new_email: string, password: string): Promise<{ success: boolean; message: string }> {
    return this.request('/user/email', {
      method: 'PUT',
      body: JSON.stringify({ new_email, password }),
    });
  }

  // Player endpoints
  async getPlayers(): Promise<{ success: boolean; players: Player[]; count: number }> {
    return this.request('/players');
  }

  async createPlayer(data: {
    name: string;
    favorite_position?: string | null;
  }): Promise<{ success: boolean; player: Player }> {
    return this.request('/players', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlayer(
    id: string,
    data: {
      name?: string;
      favorite_position?: string | null;
      attributes?: any;
    }
  ): Promise<{ success: boolean; player: Player }> {
    return this.request(`/players/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePlayer(id: string): Promise<{ success: boolean }> {
    return this.request(`/players/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkDeletePlayers(player_ids: string[]): Promise<{ success: boolean; deleted_count: number }> {
    return this.request('/players/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ player_ids }),
    });
  }

  async importCSV(formData: FormData): Promise<{
    success: boolean;
    imported: number;
    failed: number;
    errors: Array<{ row: number; name: string; reason: string }>;
  }> {
    const url = `${API_BASE_URL}/players/import-csv`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = await response.json();
      throw new Error(error.error.message || 'Import failed');
    }

    return await response.json();
  }

  async exportCSV(): Promise<Blob> {
    const url = `${API_BASE_URL}/players/export-csv`;
    const response = await fetch(url, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  }

  // Team generation endpoints
  async generateTeams(data: {
    player_ids: string[];
    team_size: TeamSize;
    current_teams?: {
      team_a: any[];
      team_b: any[];
    };
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<{ success: boolean } & GeneratedTeams> {
    return this.request('/teams/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateTeamsV2(data: {
    player_ids: string[];
    team_size: TeamSize;
    current_teams?: {
      team_a: any[];
      team_b: any[];
    };
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<{ success: boolean } & GeneratedTeams> {
    return this.request('/teams/generate-v2', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateTeamsV3(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<GeneratedTeams> {
    const response: any = await this.request('/teams/generate-v3', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // V3 returns data nested in data property
    return response.data;
  }

  async generateTeamsSuper(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<GeneratedTeams> {
    const response: any = await this.request('/teams/generate-super', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Super returns data nested in data property
    return response.data;
  }

  async generateTeamsV4Beta(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<GeneratedTeams> {
    const response: any = await this.request('/teams/generate-v4-beta', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // V4 BETA returns data nested in data property
    return response.data;
  }

  // Match history endpoints
  async getMatches(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    matches: MatchListItem[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    const endpoint = query.toString() ? `/matches?${query}` : '/matches';
    return this.request(endpoint);
  }

  async getMatch(id: string): Promise<{ success: boolean; match: Match }> {
    return this.request(`/matches/${id}`);
  }

  async saveMatch(data: {
    date: string;
    team_size: TeamSize;
    generation_method: 'AUTO' | 'MANUAL';
    generation_model?: string;
    teams: any;
    statistics: any;
    balance: any;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<{ success: boolean; match: Match }> {
    return this.request('/matches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMatchScore(
    id: string,
    data: { score_team_a: number; score_team_b: number }
  ): Promise<{ success: boolean; match: Match }> {
    return this.request(`/matches/${id}/score`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMatch(id: string): Promise<{ success: boolean }> {
    return this.request(`/matches/${id}`, {
      method: 'DELETE',
    });
  }


  async deleteAccount(password: string): Promise<{ success: boolean; message: string }> {
    return this.request('/user/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  }

  async getBetaModeEnabled(): Promise<{ success: boolean; beta_mode_enabled: boolean }> {
    return this.request('/user/beta-mode');
  }

  async updateBetaModeEnabled(beta_mode_enabled: boolean): Promise<{ success: boolean; message: string }> {
    return this.request('/user/beta-mode', {
      method: 'PUT',
      body: JSON.stringify({ beta_mode_enabled }),
    });
  }

  async getAlgorithmVisibility(): Promise<{ success: boolean; data: any }> {
    return this.request('/user/algorithm-visibility');
  }

  async updateAlgorithmVisibility(settings: any): Promise<{ success: boolean; message: string }> {
    return this.request('/user/algorithm-visibility', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async updatePositionPreference(playerId: string, positionPreference: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/players/${playerId}/position-preference`, {
      method: 'PUT',
      body: JSON.stringify({ position_preference: positionPreference }),
    });
  }

  // Player card endpoints
  async uploadPlayerCard(playerId: string, imageBase64: string): Promise<{ success: boolean; message: string; has_card: boolean }> {
    return this.request(`/players/${playerId}/card`, {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    });
  }

  async getPlayerCard(playerId: string): Promise<{ success: boolean; image: string; has_card: boolean }> {
    return this.request(`/players/${playerId}/card`);
  }

  async deletePlayerCard(playerId: string): Promise<{ success: boolean; message: string; has_card: boolean }> {
    return this.request(`/players/${playerId}/card`, {
      method: 'DELETE',
    });
  }

  async generateTeamsSuperBeta(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<GeneratedTeams> {
    const response: any = await this.request('/teams/generate-super-beta', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // Super BETA returns data nested in data property
    return response.data;
  }

  async generateTeamsV5(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<any> {
    const response: any = await this.request('/teams/generate-v5', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // V5 returns data nested in data property
    return response.data;
  }

  async generateTeamsDraft(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<any> {
    const response: any = await this.request('/teams/generate-draft', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }


  async generateTeamsV6(data: {
    player_ids: string[];
    team_size: TeamSize;
    dedicated_gk_team_a?: string | null;
    dedicated_gk_team_b?: string | null;
  }): Promise<any> {
    const response: any = await this.request('/teams/generate-v6', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async getCalibrationStatus(): Promise<{
    is_calibrated: boolean;
    matches_analyzed: number;
    confidence: number;
    category_weights: any;
    last_calibrated: string | null;
  }> {
    const response: any = await this.request('/teams/calibration/status');
    return response.data;
  }

  async triggerCalibration(): Promise<{
    category_weights: any;
    confidence: number;
    matches_analyzed: number;
    league_insights: any;
  }> {
    const response: any = await this.request('/teams/calibration/recalibrate', {
      method: 'POST',
    });
    return response.data;
  }
  // Chemistry endpoints
  async getChemistryMatrix(): Promise<{
    matrix: Array<{
      playerAId: string;
      playerAName: string;
      playerBId: string;
      playerBName: string;
      chemistryScore: number;
      manualAdjustment: number;
      effectiveScore: number;
      matchesTogether: number;
      winsTogether: number;
      goalsFor: number;
      goalsAgainst: number;
      lastPlayedTogether: string | null;
    }>;
    players: Array<{ id: string; name: string }>;
  }> {
    return this.request('/chemistry/matrix');
  }

  async getChemistryInsights(): Promise<{
    topPartnerships: any[];
    bottomPartnerships: any[];
    statistics: {
      totalPartnerships: number;
      avgChemistry: number;
      distribution: {
        excellent: number;
        good: number;
        fair: number;
        poor: number;
      };
    };
  }> {
    return this.request('/chemistry/insights');
  }

  async resetChemistry(): Promise<{ success: boolean; message: string }> {
    return this.request('/chemistry/reset', {
      method: 'DELETE',
    });
  }

  // Watch endpoints
  async getTodayMatches(): Promise<{ success: boolean; matches: any[]; count: number }> {
    return this.request('/watch/matches/today');
  }

  async startMatch(matchId: string, deviceId?: string): Promise<{ success: boolean; session_id: string; match: any; message: string }> {
    return this.request(`/watch/matches/${matchId}/start`, {
      method: 'POST',
      body: JSON.stringify({ device_id: deviceId || 'web' }),
    });
  }

  async recordEvent(matchId: string, data: {
    event_type: 'goal' | 'assist';
    team: 'A' | 'B';
    player_id: string;
    related_player_id?: string;
    match_time_seconds?: number;
    device_id?: string;
  }): Promise<{ success: boolean; event_id: string; message: string }> {
    return this.request(`/watch/matches/${matchId}/events`, {
      method: 'POST',
      body: JSON.stringify({ ...data, device_id: data.device_id || 'web' }),
    });
  }

  async getLiveMatch(matchId: string): Promise<{
    success: boolean;
    match_id: string;
    team_a_score: number;
    team_b_score: number;
    elapsed_time_seconds: number;
    is_active: boolean;
    events: any[];
  }> {
    return this.request(`/watch/matches/${matchId}/live`);
  }

  async endMatch(matchId: string, sessionId: string): Promise<{ success: boolean; total_events: number; message: string }> {
    return this.request(`/watch/matches/${matchId}/end`, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  async undoEvent(eventId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/watch/events/${eventId}`, {
      method: 'DELETE',
    });
  }

  // Stats endpoints
  async getPlayerStats(): Promise<{
    success: boolean;
    stats: Array<{
      id: string;
      name: string;
      matches_played: number;
      wins: number;
      losses: number;
      draws: number;
      win_rate: number;
      goals_for: number;
      goals_against: number;
      goal_difference: number;
      goals: number;
      assists: number;
      contributions: number;
    }>;
  }> {
    return this.request('/stats/players');
  }

  async getPlayerPairingMatrix(): Promise<{
    success: boolean;
    players: Array<{ id: string; name: string }>;
    pairings: Array<{
      player1Id: string;
      player1Name: string;
      player2Id: string;
      player2Name: string;
      totalMatchesTogether: number;
      timesOnSameTeam: number;
      percentage: number;
    }>;
  }> {
    return this.request('/stats/pairing-matrix');
  }
}

export const apiClient = new ApiClient();
